import { ReactElement } from 'react';
import { Card, Typography } from 'antd';
import { useToggle } from 'react-use';
import EditButton from './EditButton';
import SummaryForm from './SummaryForm';
import SummaryDisplay from './SummaryDisplay';

const { Text } = Typography;

const RECORDING_STATUS = {
  PROCESSING: 1,
  COMPLETED: 2,
  FAILED: 3,
} as const;

interface Props {
  summaryText: string | null;
  recordingStatusId: number;
  onUpdate: (summaryText: string) => Promise<{ success: boolean }>;
}

const Summary = ({ summaryText, recordingStatusId, onUpdate }: Props): ReactElement => {
  const [isEditMode, toggleEditMode] = useToggle(false);
  const [isSaving, setSaving] = useToggle(false);

  const handleSave = async (value: string): Promise<void> => {
    setSaving(true);
    const result = await onUpdate(value);
    setSaving(false);

    if (result.success) {
      toggleEditMode(false);
    }
  };

  const handleEditButtonClick = (): void => {
    toggleEditMode(true);
  };

  const handleCancel = (): void => {
    toggleEditMode(false);
  };

  const handleFormSave = (value: string): void => {
    void handleSave(value);
  };

  const isEditable = recordingStatusId === RECORDING_STATUS.COMPLETED;

  const renderContent = (): ReactElement => {
    if (recordingStatusId === RECORDING_STATUS.PROCESSING) {
      return <Text type='secondary'>文字起こし・要約を処理中です</Text>;
    }

    if (recordingStatusId === RECORDING_STATUS.FAILED) {
      return <Text type='danger'>処理に失敗しました</Text>;
    }

    if (isEditMode) {
      return (
        <SummaryForm
          defaultValue={summaryText ?? ''}
          onSave={handleFormSave}
          onCancel={handleCancel}
          loading={isSaving}
        />
      );
    }

    return <SummaryDisplay content={summaryText ?? ''} />;
  };

  return (
    <Card
      title='要約'
      size='small'
      extra={!isEditMode && isEditable ? <EditButton onClick={handleEditButtonClick} /> : null}
    >
      {renderContent()}
    </Card>
  );
};

export default Summary;
